import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { showErrorToast } from '../../utils/errorHandler';
import { Bike, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const BikerLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phone || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone format (9 digits without prefix)
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 9) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 9-digit phone number',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.post('/auth/login', {
        phone: `+260${cleanPhone}`,
        password: formData.password,
      });

      const { token, user } = response.data;

      // Store token and user data
      localStorage.setItem('tekstar_token', token);
      localStorage.setItem('tekstar_user', JSON.stringify(user));

      toast({
        title: 'Welcome back!',
        description: 'Logged in successfully',
      });

      // Check if user has driver profile and if it's a bike
      try {
        const driverResponse = await api.get('/bikers/my-profile');
        
        if (driverResponse.data?.driver) {
          const vehicleType = driverResponse.data.driver.vehicleType?.toLowerCase();
          
          // Check if it's a bike/motorbike
          if (vehicleType === 'motorbike' || vehicleType === 'bike' || vehicleType === 'motor bike') {
            navigate('/biker/dashboard');
          } else {
            // Not a biker, redirect to driver dashboard
            toast({
              title: 'Wrong Portal',
              description: 'Please use the driver portal for car drivers',
              variant: 'destructive',
            });
            navigate('/driver/dashboard');
          }
        } else {
          // No driver profile, redirect to application
          toast({
            title: 'No Biker Profile',
            description: 'Please complete your biker application',
            variant: 'destructive',
          });
          navigate('/biker/apply');
        }
      } catch (error) {
        // If 404, user needs to apply as biker
        if (error.response?.status === 404) {
          toast({
            title: 'Biker Application Required',
            description: 'Please complete your biker application to continue',
          });
          navigate('/biker/apply');
        } else {
          // Other error, still redirect to dashboard
          navigate('/biker/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showErrorToast(toast, error, 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 flex items-center justify-center px-4 pt-20 pb-8">
      <Card className="w-full max-w-md p-8">
        {/* Back to main site link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to main site
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-full mb-4">
            <Bike className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Biker Portal</h1>
          <p className="text-gray-600">Sign in to start delivering with Tekstar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
              <Input
                id="phone"
                type="tel"
                className="rounded-l-none"
                placeholder="XXX XXX XXX"
                maxLength={9}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white py-6 text-lg font-semibold"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in as Biker'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-orange-600 hover:text-orange-700 font-medium">
              Sign up first
            </Link>
          </p>
        </div>

        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Want to become a biker?</span>
            </div>
          </div>

          <Link to="/biker/apply">
            <Button
              type="button"
              variant="outline"
              className="w-full"
            >
              Apply to Bike
            </Button>
          </Link>
          <p className="text-xs text-center text-gray-500">
            Note: You need an account to apply. Sign up first if you don't have one.
          </p>
        </div>

        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-600">
            Car driver?{' '}
            <Link to="/driver/login" className="text-purple-600 hover:text-purple-700 font-medium">
              Sign in here
            </Link>
          </p>
          <p className="text-sm text-gray-600">
            Not a biker?{' '}
            <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
              Sign in as rider
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default BikerLogin;
